import * as React from 'react';
import {
  LayoutGrid, List, Download, Eye, Heart, MessageSquare, Bookmark, Share2, Repeat,
  Video, Image as ImageIcon, FileText, Loader2, X, ChevronLeft, ChevronRight,
  RefreshCw, Tag, Plus, ExternalLink, Sparkles, TrendingUp, Zap, Play, Pause,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { getLibrary, getMe, listTags, createTag, setLibraryTags, extractContent, getToken, startRewrite, getRewriteStatus, getInteractionTrend, type InteractionTrendPoint } from '@/api/client';
import ViralFetchModal from '@/components/viral-fetch-modal';

type TagItem = { id: number; name: string; color: string };
type LibraryItem = {
  id: number; video_id: string; platform: string; account_uid: string; account_name: string;
  title: string; description: string; cover_url: string; play_count: number; digg_count: number;
  comment_count: number; share_count: number; collect_count: number; duration: number;
  media_type: string; transcript: string | null; transcribe_status: string | null;
  download_status: string | null; create_time: string; added_at: string; source: string;
  user_name: string; user_username: string; tags: TagItem[]; image_list: string[];
  user_id: number; deleted: number; video_url?: string;
};

const PLATFORM_INFO: Record<string, { label: string; color: string }> = {
  douyin: { label: '抖音', color: 'bg-sky-500' },
  xiaohongshu: { label: '小红书', color: 'bg-pink-500' },
  wechat: { label: '视频号', color: 'bg-emerald-500' },
  finder: { label: '视频号', color: 'bg-emerald-500' },
};

function platformBadge(p: string, label?: string) {
  const i = PLATFORM_INFO[p]; if (!i) return <Badge variant="outline">{label || p}</Badge>;
  // 2026-09-17：50% 不透明彩色底 + 白字（80% 偏高已回调，原 10% 透明底+彩色字不明显），抖音/小红书/视频号统一
  const cls = `${i.color === 'bg-sky-500' ? 'bg-sky-500/50 text-white' : i.color === 'bg-pink-500' ? 'bg-pink-500/50 text-white' : 'bg-emerald-500/50 text-white'}`;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}><span className="h-1.5 w-1.5 rounded-full bg-white/90" />{label || i.label}</span>;
}

/** 封面取值：cover_url 优先，体检同步等来源常只有 image_list（cover_url 为空），用首图兜底（与诊断页 WorksGrid 一致） */
function coverOf(item: { cover_url?: string; image_list?: string[] }): string {
  return item.cover_url || (item.image_list && item.image_list.length ? item.image_list[0] : '') || '';
}

/** 封面/图片加载兜底：远程直链过期（小红书 rednotecdn 498、抖音 douyinpic 过期 403 等）
 * 时显示占位图标而非裂图；src 变化时重置失败态（2026-09-17 谷雨小红书封面 498 案例） */
function CoverImg({ src, alt = '', className = '', mediaType = 'video' }: { src: string; alt?: string; className?: string; mediaType?: string }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) {
    return (
      <div className={`flex h-full items-center justify-center text-muted-foreground/50 ${className}`}>
        {mediaType === 'video' ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
      </div>
    );
  }
  return <img src={src} alt={alt} referrerPolicy="no-referrer" onError={() => setFailed(true)} className={className} />;
}

/** 图文轮播：多图左右切换 + 页码指示 + 缩略图点选（抖音/小红书图文预览，2026-09-17） */
function ImageCarousel({ images, poster }: { images: string[]; poster?: string }) {
  const [idx, setIdx] = React.useState(0);
  const imgs = images && images.length ? images : poster ? [poster] : [];
  if (!imgs.length) return null;
  const cur = Math.min(idx, imgs.length - 1);
  return (
    <div className="relative flex h-full max-h-[42vh] w-full flex-col overflow-hidden rounded-lg lg:max-h-full">
      <div className="relative flex min-h-0 flex-1 items-center justify-center bg-black">
        <CoverImg
          key={cur}
          src={imgs[cur]}
          alt={`图文 ${cur + 1}/${imgs.length}`}
          mediaType="image"
          className="max-h-full max-w-full object-contain"
        />
        {imgs.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setIdx((cur - 1 + imgs.length) % imgs.length); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/50 p-1.5 text-white/90 transition hover:bg-black/70"
              aria-label="上一张"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setIdx((cur + 1) % imgs.length); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/50 p-1.5 text-white/90 transition hover:bg-black/70"
              aria-label="下一张"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white/90">
              {cur + 1} / {imgs.length}
            </span>
          </>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 bg-black/60 px-2 py-1.5">
          {imgs.map((u, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-8 w-8 overflow-hidden rounded border transition ${i === cur ? 'border-white/80' : 'border-white/20 opacity-60 hover:opacity-100'}`}
              aria-label={`第 ${i + 1} 张`}
            >
              <CoverImg src={u} mediaType="image" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(n: number | null | undefined): string {
  if (n == null || n <= 0) return '—'; if (n >= 10000) return `${(n / 10000).toFixed(1)}万`; if (n >= 1000) return `${(n / 1000).toFixed(1)}k`; return String(n);
}

/** 图文背景音乐：紧凑胶囊播放按钮（不占画面空间，2026-09-17）。
 *  优先本地已下载的 mp3（serve_video 返回 audio/mpeg），否则用直链（抖音 v3-music 签名 URL）。 */
function MusicToggle({ src }: { src: string }) {
  const ref = React.useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.play().then(() => setPlaying(true)).catch((e: unknown) => {
      // 自动播放策略拒绝（无用户手势，如脚本触发/静音策略）不是音频源错误，
      // 不标记永久失效，用户下次真实点击仍可播放
      if (e && typeof e === 'object' && (e as { name?: string }).name === 'NotAllowedError') return;
      setFailed(true);
    });
  };
  return (
    <>
      <audio ref={ref} src={src} preload="none" onEnded={() => setPlaying(false)} onError={() => setFailed(true)} />
      <button
        onClick={toggle}
        disabled={failed}
        className={`inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-medium transition ${failed ? 'cursor-not-allowed border-destructive/40 text-destructive/70' : playing ? 'border-primary/60 bg-background text-primary' : 'border-border bg-background text-muted-foreground hover:text-foreground'}`}
        title={failed ? '音乐不可用' : (playing ? '暂停背景音乐' : '播放背景音乐')}
      >
        {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        {failed ? '音乐失效' : '音乐'}
      </button>
    </>
  );
}

/** 标签最多显示 2 行（leading-[15px] 行高 15px + py-0.5 上下各 2px + gap-1 4px = 42px 精确两行），超出裁剪并在右下角 +N；
 *  鼠标悬停时跟随指针弹出全部标签小窗（fixed 定位，防被卡片 overflow-hidden 裁剪） */
function ClampedTags({ tags }: { tags: TagItem[] }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = React.useState(false);
  const [hidden, setHidden] = React.useState(0);
  const [tipPos, setTipPos] = React.useState<{ x: number; y: number } | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const kids = Array.from(el.querySelectorAll('[data-clamped-tag]')) as HTMLElement[];
      const er = el.getBoundingClientRect();
      let visible = 0;
      for (const k of kids) {
        const r = k.getBoundingClientRect();
        if (r.bottom <= er.bottom + 0.5 && r.top >= er.top - 0.5) visible++;
        else break;
      }
      setHidden(kids.length - visible);
      setOverflow(kids.length > visible);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tags]);

  return (
    <div className="relative">
      <div
        ref={ref}
        className="flex flex-wrap gap-1 overflow-hidden"
        style={{ maxHeight: 42 }}
        onMouseEnter={(e) => setTipPos({ x: e.clientX + 14, y: e.clientY + 14 })}
        onMouseMove={(e) => setTipPos({ x: e.clientX + 14, y: e.clientY + 14 })}
        onMouseLeave={() => setTipPos(null)}
      >
        {tags.map(t => (
          <span key={t.id} data-clamped-tag className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium leading-[15px]" style={{ backgroundColor: t.color + '20', color: t.color }}>{t.name}</span>
        ))}
      </div>
      {overflow && hidden > 0 && (
        <span className="pointer-events-none absolute bottom-0 right-0 rounded-sm bg-background px-1 text-[9px] font-semibold text-muted-foreground shadow-sm">+{hidden}</span>
      )}
      {tipPos && overflow && (
        <div className="fixed z-[100] max-h-[240px] max-w-[280px] overflow-y-auto rounded-lg border bg-popover p-2 shadow-xl" style={{ left: Math.min(tipPos.x, window.innerWidth - 300), top: Math.min(tipPos.y, window.innerHeight - 120) }}>
          <div className="flex flex-wrap gap-1">
            {tags.map(t => (
              <span key={t.id} className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: t.color + '20', color: t.color }}>{t.name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** 标题截断（line-clamp-N，lines=1 列表行 / 2 网格卡片）：截断时 hover 跟随指针弹出完整标题小窗（同 ClampedTags 模式，scrollHeight 判定截断） */
function ClampedTitle({ text, lines }: { text: string; lines?: 1 | 2 }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = React.useState(false);
  const [tipPos, setTipPos] = React.useState<{ x: number; y: number } | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setTruncated(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, lines]);

  return (
    <>
      <div
        ref={ref}
        className={lines === 1 ? 'line-clamp-1 text-sm font-medium' : 'line-clamp-2 text-sm font-medium leading-snug'}
        onMouseEnter={(e) => setTipPos({ x: e.clientX + 14, y: e.clientY + 14 })}
        onMouseMove={(e) => setTipPos({ x: e.clientX + 14, y: e.clientY + 14 })}
        onMouseLeave={() => setTipPos(null)}
      >{text}</div>
      {tipPos && truncated && (
        <div className="pointer-events-none fixed z-[100] max-h-[240px] max-w-[320px] overflow-y-auto break-words rounded-lg border bg-popover p-2 text-sm shadow-xl" style={{ left: Math.min(tipPos.x, window.innerWidth - 340), top: Math.min(tipPos.y, window.innerHeight - 120) }}>
          {text}
        </div>
      )}
    </>
  );
}

/** 文字大小的圆形下载进度环（pending=0% / downloading=旋转 / downloaded=100% / failed=红色!） */
function DownloadRingMini({ status }: { status: string }) {
  const CIRC = 2 * Math.PI * 6.5;
  const pct = status === 'downloaded' ? 100 : status === 'downloading' ? 45 : 0;
  const failed = status === 'failed';
  return (
    <svg viewBox="0 0 16 16" className={`h-3.5 w-3.5 shrink-0 ${status === 'downloading' ? 'animate-spin' : ''}`}>
      <circle cx="8" cy="8" r="6.5" fill="none" strokeWidth="2.2" className="stroke-muted-foreground/30" />
      {failed ? (
        <text x="8" y="10.8" textAnchor="middle" fontSize="10" className="fill-destructive font-bold">!</text>
      ) : (
        <circle
          cx="8" cy="8" r="6.5" fill="none" strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * CIRC} ${CIRC}`}
          transform="rotate(-90 8 8)"
          className={status === 'downloaded' ? 'stroke-emerald-500' : 'stroke-primary'}
        />
      )}
    </svg>
  );
}

// 时长：后端已统一存秒；防御毫秒值（>10000 视为毫秒转秒）
function fmtDuration(d: number | null | undefined): string {
  if (!d || d <= 0) return '';
  let s = d;
  if (s > 10000) s = Math.floor(s / 1000);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// 用户已删除灰色标签（封面右上角）
function deletedBadge() {
  return <span className="absolute right-1.5 top-1.5 rounded bg-gray-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">用户已删除</span>;
}

type StatSpec = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
};

/** 按平台返回应展示的数据指标（小红书/视频号没有播放量，显示各自的互动指标） */
function platformStats(item: LibraryItem): StatSpec[] {
  const p = item.platform;
  if (p === 'xiaohongshu') {
    return [
      { icon: Heart, label: '点赞', value: item.digg_count },
      { icon: Bookmark, label: '收藏', value: item.collect_count },
      { icon: MessageSquare, label: '评论', value: item.comment_count },
      { icon: Share2, label: '分享', value: item.share_count },
    ];
  }
  if (p === 'wechat' || p === 'finder') {
    return [
      { icon: Heart, label: '点赞', value: item.digg_count },
      { icon: MessageSquare, label: '评论', value: item.comment_count },
      { icon: Repeat, label: '转发', value: item.share_count },
    ];
  }
  return [
    { icon: Eye, label: '播放', value: item.play_count },
    { icon: Heart, label: '点赞', value: item.digg_count },
    { icon: MessageSquare, label: '评论', value: item.comment_count },
  ];
}

/** 列表视图第二列指标（点赞 固定第一列） */
function secondStat(item: LibraryItem): { label: string; value: number } {
  const p = item.platform;
  if (p === 'xiaohongshu') return { label: '收藏', value: item.collect_count };
  if (p === 'wechat' || p === 'finder') return { label: '转发', value: item.share_count };
  return { label: '播放', value: item.play_count };
}

function fmtTime(t: string | null | undefined): string {
  if (!t) return '—'; const d = new Date(t); if (isNaN(d.getTime())) return t; return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** 快照指标字段映射（平台展示标签 → interaction_snapshots 字段） */
const TREND_KEY: Record<string, keyof InteractionTrendPoint> = {
  '点赞': 'digg_count',
  '收藏': 'collect_count',
  '评论': 'comment_count',
  '分享': 'share_count',
  '转发': 'share_count',
  '播放': 'play_count',
};

/** 采样日期短格式（如 9/11） */
function fmtDay(t: string | null | undefined): string {
  if (!t) return '—'; const d = new Date(t); if (isNaN(d.getTime())) return t.slice(5, 10); return d.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });
}

// ─── 互动数据趋势曲线（免费指标快照） ─────────────────────────────

/** 内容详情互动趋势：原生 SVG 迷你折线（点赞/评论两条线）+ 涨跌徽标 + y 轴刻度 + x 轴采样日期 + 线尾最新值 + hover 数值浮层。
 *  受控组件：points/loading/error 由父级（详情弹窗）统一 fetch，供顶部互动行「较首采」小字共用同一份数据。 */
function TrendChartMini({ points, loading, error }: { points: InteractionTrendPoint[]; loading: boolean; error: string }) {
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);

  if (loading) return <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground"><Loader2 className="mr-1 h-3 w-3 animate-spin" />加载趋势…</div>;
  if (error) return <div className="flex h-full items-center justify-center text-[11px] text-destructive">趋势加载失败</div>;
  if (!points.length) return <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground">暂无趋势数据（监控采样后自动生成）</div>;

  const n = points.length;
  const maxV = Math.max(1, ...points.map((p) => Math.max(p.digg_count || 0, p.comment_count || 0)));
  if (maxV <= 1) return <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground">暂无有效数据（指标均为 0）</div>;

  const yOf = (key: 'digg_count' | 'comment_count', i: number) => 40 - ((points[i][key] || 0) / maxV) * 36 - 2;
  const low = n <= 2;
  const polyPts = (key: 'digg_count' | 'comment_count') => points.map((_, i) => {
    const x = n > 1 ? (i / (n - 1)) * 100 : 50;
    return `${x},${yOf(key, i)}`;
  }).join(' ');

  // 涨跌徽标：vs 上次采样（点赞+评论合计）
  let badge = null;
  if (!low) {
    const last = points[n - 1], prev = points[n - 2];
    const delta = ((last.digg_count || 0) + (last.comment_count || 0)) - ((prev.digg_count || 0) + (prev.comment_count || 0));
    if (delta > 0) badge = <span className="rounded bg-emerald-500/10 px-1 text-[10px] font-medium text-emerald-500" title="较上次采样">↑{delta}</span>;
    else if (delta < 0) badge = <span className="rounded bg-red-500/10 px-1 text-[10px] font-medium text-red-500" title="较上次采样">↓{Math.abs(delta)}</span>;
    else badge = <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground" title="较上次采样">→ 持平</span>;
  }

  const lastIdx = n - 1;
  const dY = yOf('digg_count', lastIdx) / 40 * 100; // 点赞线尾位置（%）
  let cY = yOf('comment_count', lastIdx) / 40 * 100; // 评论线尾位置（%）
  if (Math.abs(cY - dY) < 12) cY = Math.min(94, cY + 12); // 两条线尾重叠时评论标签下移
  const mid = Math.floor(n / 2);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border bg-background p-2">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-medium"><TrendingUp className="h-3.5 w-3.5 text-primary" />互动趋势</div>
        <div className="flex items-center gap-1.5">
          {badge}
          <span className="text-[10px] text-muted-foreground">{n} 点</span>
        </div>
      </div>
      {low ? (
        <div className="flex min-h-0 w-full flex-1 items-center justify-center">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {Array.from({ length: n }).map((_, i) => (
              <span key={i} className="h-2 w-2 shrink-0 rounded-full" style={{ background: 'var(--color-chart-1)' }} />
            ))}
            仅 {n} 个采样点，监控积累后显示曲线
          </span>
        </div>
      ) : (
        <>
          <div className="relative flex min-h-0 flex-1">
            {/* y 轴刻度标签（左列，w-8 对齐网格线 5%/50%/95%） */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 text-right text-[9px] leading-none text-muted-foreground/70">
              <span className="absolute right-0 -translate-y-1/2" style={{ top: '5%' }}>{fmt(maxV)}</span>
              <span className="absolute right-0 -translate-y-1/2" style={{ top: '50%' }}>{fmt(Math.round(maxV / 2))}</span>
              <span className="absolute right-0 -translate-y-1/2" style={{ top: '95%' }}>0</span>
            </div>
            {/* 线尾最新值标签（右列，w-6） */}
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 text-[9px] leading-none">
              <span className="absolute left-0 -translate-y-1/2 font-medium" style={{ top: `${dY}%`, color: 'var(--color-chart-1)' }}>{fmt(points[lastIdx].digg_count || 0)}</span>
              <span className="absolute left-0 -translate-y-1/2 font-medium" style={{ top: `${cY}%`, color: 'var(--color-chart-3)' }}>{fmt(points[lastIdx].comment_count || 0)}</span>
            </div>
            {/* hover 数值浮层 */}
            {hoverIdx != null && (
              <div
                className="pointer-events-none absolute top-0 z-20 -translate-x-1/2 rounded border bg-background px-1.5 py-0.5 text-[9px] text-muted-foreground shadow-sm"
                style={{ left: `calc(2rem + (100% - 3.5rem) * ${hoverIdx / (n - 1)})` }}
              >
                {fmtDay(points[hoverIdx].captured_at)} 赞{points[hoverIdx].digg_count || 0} 评{points[hoverIdx].comment_count || 0}
              </div>
            )}
            {/* 图表区：外层 pl-8 pr-6 让出刻度/线尾列，SVG 本身无 padding → onMouseMove 映射精确 */}
            <div className="h-full w-full pl-8 pr-6">
              <svg
                viewBox="0 0 100 40"
                preserveAspectRatio="none"
                className="h-full w-full"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  setHoverIdx(Math.max(0, Math.min(n - 1, Math.round((x / 100) * (n - 1)))));
                }}
                onMouseLeave={() => setHoverIdx(null)}
              >
                {[38, 20, 2].map((y) => (
                  <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="var(--color-border)" strokeWidth="0.5" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
                ))}
                <polyline points={polyPts('digg_count')} fill="none" stroke="var(--color-chart-1)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                <polyline points={polyPts('comment_count')} fill="none" stroke="var(--color-chart-3)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              </svg>
            </div>
          </div>
          {/* x 轴采样日期（首/中/尾，对齐图表内容区） */}
          <div className="mt-0.5 flex justify-between pl-8 pr-6 text-[9px] text-muted-foreground/70">
            <span>{fmtDay(points[0].captured_at)}</span>
            <span>{fmtDay(points[mid].captured_at)}</span>
            <span>{fmtDay(points[lastIdx].captured_at)}</span>
          </div>
        </>
      )}
      <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
        <span><i className="mr-1 inline-block h-0.5 w-3 align-middle" style={{ background: 'var(--color-chart-1)' }} />点赞</span>
        <span><i className="mr-1 inline-block h-0.5 w-3 align-middle" style={{ background: 'var(--color-chart-3)' }} />评论</span>
      </div>
    </div>
  );
}

export default function LibraryPage() {
  const [items, setItems] = React.useState<LibraryItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(30);
  const [jumpPage, setJumpPage] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [platformFilter, setPlatformFilter] = React.useState('');
  const [sortBy, setSortBy] = React.useState('create_time');
  const [viewMode, setViewMode] = React.useState<'card' | 'list'>('card');
  const [previewItem, setPreviewItem] = React.useState<LibraryItem | null>(null);
  // 互动趋势快照（详情弹窗统一 fetch：趋势图 + 顶部互动行「较首采」小字共用）
  const [trend, setTrend] = React.useState<{ points: InteractionTrendPoint[]; loading: boolean; error: string }>({ points: [], loading: false, error: '' });
  React.useEffect(() => {
    if (!previewItem) { setTrend({ points: [], loading: false, error: '' }); return; }
    let cancelled = false;
    setTrend({ points: [], loading: true, error: '' });
    // userId 可选：admin 查看他人内容时按内容归属用户查趋势（后端 admin 传 user_id 生效，普通用户强制用自己 id）
    getInteractionTrend({ video_id: previewItem.video_id, user_id: previewItem.user_id, limit: 90 })
      .then((d) => { if (!cancelled) setTrend({ points: d.points || [], loading: false, error: '' }); })
      .catch((e) => { if (!cancelled) setTrend({ points: [], loading: false, error: e instanceof Error ? e.message : '加载失败' }); });
    return () => { cancelled = true; };
  }, [previewItem]);
  const [extractingText, setExtractingText] = React.useState(false);
  const [extractMsg, setExtractMsg] = React.useState('');

  // 预览弹窗左媒体栏：lg+ 宽度 = 实际可用高度 × 媒体比例（视频 9:16 / 图文 3:4），保证竖版媒体撑满无黑边
  // 注意：不能 ResizeObserver 观察媒体容器——回调改 col 宽度 → box 宽度跟着变 → 再触发 RO → 宽度在多个值间震荡，
  // 弹窗反复放大缩小闪烁（图文多图 grid 列数取整使 box 高度非线性跳变，震荡区间极宽）。只在打开时算一次 + window resize 重算。
  const mediaColRef = React.useRef<HTMLDivElement | null>(null);
  const mediaBoxRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const col = mediaColRef.current, box = mediaBoxRef.current;
    if (!col || !box) return;
    const apply = () => {
      if (window.innerWidth < 1024) { col.style.width = ''; return; }
      const ratio = previewItem?.media_type === 'video' ? 9 / 16 : 3 / 4;
      const h = box.clientHeight;
      if (h > 0) col.style.width = `${Math.round(h * ratio + 32)}px`; // +32 = lg:p-4 左右 padding
    };
    apply();
    window.addEventListener('resize', apply);
    return () => { window.removeEventListener('resize', apply); };
  }, [previewItem]);

  // ─── 下载/转录状态（只读展示，2026-09-15 起入库即下载、下载即转录，无手动按钮）───────
  const statusText = (item: LibraryItem) => {
    const st = item.download_status || 'pending';
    if (st === 'downloaded') return { text: '已加载', cls: 'text-emerald-500' };
    if (st === 'downloading') return { text: '后台加载中…', cls: 'text-primary' };
    if (st === 'failed') return { text: '加载失败（系统将自动重试）', cls: 'text-destructive' };
    return { text: '等待加载…', cls: 'text-muted-foreground' };
  };

  // 用户身份
  const [currentUser, setCurrentUser] = React.useState<any>(null);
  const [users, setUsers] = React.useState<{ id: number; username: string; display_name: string }[]>([]);
  const [filterUserId, setFilterUserId] = React.useState<number>(0);
  const isAdmin = currentUser?.role === 'admin';

  // 标签系统
  const [allTags, setAllTags] = React.useState<TagItem[]>([]);
  const [filterTag, setFilterTag] = React.useState('');
  const [tagEditOpen, setTagEditOpen] = React.useState(false);
  const [editItemTags, setEditItemTags] = React.useState<TagItem[]>([]);
  const [editItemId, setEditItemId] = React.useState<number | null>(null);
  const [newTagName, setNewTagName] = React.useState('');

  // 添加单条内容
  const [addContentOpen, setAddContentOpen] = React.useState(false);
  const [addPlatform, setAddPlatform] = React.useState('douyin');
  const [addUrl, setAddUrl] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [addMessage, setAddMessage] = React.useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // 爆款内容爬取（弹窗）
  const [viralOpen, setViralOpen] = React.useState(false);

  // 加载用户列表（管理员用）
  React.useEffect(() => {
    getMe().then((d) => {
      setCurrentUser(d.user);
      if (d.user?.role === 'admin') {
        // 加载用户列表用于筛选
        fetch('/api/admin/users').then(r => r.json()).then(d2 => setUsers(d2.users || [])).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLibrary({
        page, page_size: pageSize, platform: platformFilter, sort: sortBy, order: 'desc',
        user_id: filterUserId > 0 ? filterUserId : undefined,
        tag: filterTag || undefined,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [page, pageSize, platformFilter, sortBy, filterUserId, filterTag]);

  React.useEffect(() => { load(); }, [load]);

  // 加载标签列表
  React.useEffect(() => {
    listTags().then(d => setAllTags(d.tags || [])).catch(() => {});
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // 跳转指定页（校验 1..totalPages，回车或按钮触发）
  const doJumpPage = () => {
    const n = Number(jumpPage);
    if (Number.isInteger(n) && n >= 1 && n <= totalPages) setPage(n);
    setJumpPage('');
  };

  // ─── AI 改写 ────────────────────────────────────────────────
  const [rewriting, setRewriting] = React.useState(false);
  const [rewriteMsg, setRewriteMsg] = React.useState('');
  const [rewriteResult, setRewriteResult] = React.useState('');
  const [rewriteError, setRewriteError] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setRewriteJobId] = React.useState('');

  const handleRewrite = React.useCallback(async (item: LibraryItem) => {
    if (rewriting) return;
    // 需先有逐字稿
    if (!item.transcript) {
      setRewriteError('该内容暂无逐字稿，请先点击「转录」生成后再改写。');
      setRewriteResult('');
      setRewriteMsg('');
      return;
    }
    setRewriting(true);
    setRewriteMsg('正在启动改写任务…');
    setRewriteResult('');
    setRewriteError('');
    setRewriteJobId('');
    try {
      const d = await startRewrite(item.video_id, '', item.user_id);
      if (d.status !== 'started' || !d.job_id) {
        setRewriteError('改写任务启动失败');
        setRewriting(false);
        return;
      }
      setRewriteJobId(d.job_id);
      // 轮询进度
      let tries = 0;
      const timer = window.setInterval(async () => {
        tries += 1;
        try {
          const st = await getRewriteStatus(d.job_id);
          if (st.status === 'running') {
            setRewriteMsg(st.message || 'AI 改写中…');
          } else if (st.status === 'done') {
            window.clearInterval(timer);
            setRewriting(false);
            setRewriteMsg('');
            setRewriteResult(st.result || '');
            if (typeof st.originality === 'number') {
              setRewriteMsg(`原创度 ${st.originality}%`);
            }
          } else if (st.status === 'error') {
            window.clearInterval(timer);
            setRewriting(false);
            setRewriteMsg('');
            setRewriteError(st.error || '改写失败');
          } else if (st.status === 'not_found') {
            window.clearInterval(timer);
            setRewriting(false);
            setRewriteMsg('');
            setRewriteError('改写任务不存在，请重试');
          }
        } catch {
          // 网络抖动，继续轮询
        }
        // 超过 5 分钟仍未结束则停止
        if (tries > 100) {
          window.clearInterval(timer);
          setRewriting(false);
          setRewriteMsg('');
          setRewriteError('改写超时，请稍后重试');
        }
      }, 3000);
    } catch (err: any) {
      setRewriting(false);
      setRewriteMsg('');
      setRewriteError(err?.message || '改写启动失败');
    }
  }, [rewriting]);

  // ─── 下载 ───────────────────────────────────────────────────
  const [downloading, setDownloading] = React.useState(false);
  const [downloadMsg, setDownloadMsg] = React.useState('');

  // 下载改写文案为 txt
  const handleDownloadRewrite = React.useCallback((text: string) => {
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = `改写文案_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
  }, []);

  const handleDownload = React.useCallback(async (item: LibraryItem) => {
    if (downloading) return;
    setDownloading(true);
    setDownloadMsg('');
    try {
      const token = getToken() || '';
      const qs = [
        token ? `token=${encodeURIComponent(token)}` : '',
        item.user_id ? `user_id=${item.user_id}` : '',
      ].filter(Boolean).join('&');
      // 视频 → 后端打包 mp4+文案(逐字稿) zip；图文 → 后端打包全部图片+文案 txt zip
      const url = item.media_type === 'video'
        ? `/api/media/download/video/${item.video_id}${qs ? '?' + qs : ''}`
        : `/api/media/download/archive/${item.video_id}${qs ? '?' + qs : ''}`;

      if (item.media_type === 'video') {
        // 视频：<a> 原生下载（后端可能重定向到 CDN 真实 mp4，fetch 会被 CORS 拦）
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // 图文：先请求，校验 zip 或错误 JSON，再触发下载
        const res = await fetch(url);
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          setDownloadMsg(data?.detail || `下载失败 (${res.status})`);
          return;
        }
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `content_${item.video_id || Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }
    } catch {
      // 忽略
    } finally {
      setDownloading(false);
    }
  }, [downloading]);

  // 标签管理
  const openTagEdit = React.useCallback((item: LibraryItem) => {
    setEditItemId(item.id); setEditItemTags([...item.tags]); setTagEditOpen(true);
  }, []);
  const saveTags = React.useCallback(async () => {
    if (editItemId == null) return;
    await setLibraryTags(editItemId, editItemTags.map(t => t.id));
    setTagEditOpen(false); load();
  }, [editItemId, editItemTags, load]);
  const createNewTag = React.useCallback(async () => {
    if (!newTagName.trim()) return;
    try { const d = await createTag(newTagName.trim()); setAllTags(prev => [...prev, { id: d.id, name: newTagName.trim(), color: '#6366f1' }]); setNewTagName(''); } catch {}
  }, [newTagName]);

  // 添加单条内容
  const openAddContent = React.useCallback(() => {
    setAddContentOpen(true);
    setAddUrl('');
    setAddPlatform('douyin');
    setAddMessage(null);
  }, []);

  const handleAddContent = React.useCallback(async () => {
    if (!addUrl.trim()) { setAddMessage({ type: 'error', text: '请输入分享链接' }); return; }
    setAdding(true);
    setAddMessage(null);
    try {
      const res = await extractContent(addUrl.trim(), addPlatform);
      setAddMessage({
        type: 'success' as const,
        text: res.already ? '该内容已在库中。' : '提取成功！内容已加入内容库。',
      });
      setAddUrl('');
      setPage(1);
      load();
    } catch (err: any) {
      setAddMessage({
        type: 'error' as const,
        text: err?.message || '提取失败，请检查链接是否正确。',
      });
    } finally {
      setAdding(false);
    }
  }, [addUrl, addPlatform, load]);

  // ─── 提取文案（补全正文：小红书详情接口拉全文更新库内 description）───
  const handleExtractText = React.useCallback(async (item: LibraryItem) => {
    setExtractingText(true);
    setExtractMsg('');
    try {
      const res = await extractContent(item.video_id, item.platform);
      if (res.updated) {
        setExtractMsg('正文已补全');
        setPreviewItem(prev => prev ? { ...prev, description: res.description || prev.description } : prev);
        load();
      } else if (res.already) {
        setExtractMsg('该内容已在库中');
      } else {
        setExtractMsg('提取成功');
      }
    } catch (err: any) {
      setExtractMsg(err?.message || '提取失败，请稍后重试');
    } finally {
      setExtractingText(false);
    }
  }, [load]);

  // 渲染卡片视图
  const renderCard = (item: LibraryItem) => (
    <Card key={item.id} className="group cursor-pointer overflow-hidden transition-all hover:shadow-md" onClick={() => { setRewriteMsg(''); setRewriteResult(''); setRewriteError(''); setRewriteJobId(''); setExtractMsg(''); setPreviewItem(item); }}>
      <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '3/4' }}>
                        {coverOf(item) ? <CoverImg src={coverOf(item)} mediaType={item.media_type} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
          : <div className="flex h-full items-center justify-center">{item.media_type === 'video' ? <Video className="h-8 w-8 text-muted-foreground/50" /> : <ImageIcon className="h-8 w-8 text-muted-foreground/50" />}</div>}
        {/* 抖音图文：隐藏右下角时长标签（2026-09-17）；其余内容保留时长 */}
        {item.media_type === 'video' && item.duration ? <div className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">{fmtDuration(item.duration)}</div> : null}
        <div className="absolute left-2 top-2">
          {item.platform === 'douyin' && item.media_type === 'image'
            ? platformBadge(item.platform, '抖音图文')
            : platformBadge(item.platform)}
        </div>
        {item.deleted ? deletedBadge() : null}
      </div>
      <div className="p-3 space-y-1.5">
        <ClampedTitle text={item.title || item.description || '无标题'} lines={2} />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{item.account_name || item.account_uid || ''}</span>
          {isAdmin && item.user_name && <Badge variant="outline" className="text-[10px]">{item.user_name}</Badge>}
        </div>
        {item.tags?.length > 0 && <ClampedTags tags={item.tags} />}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {platformStats(item).map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1" title={s.label}><s.icon className="h-3 w-3" />{fmt(s.value)}</span>
          ))}
        </div>
      </div>
    </Card>
  );

  // 渲染列表行
  const renderRow = (item: LibraryItem) => (
    <tr key={item.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => { setRewriteMsg(''); setRewriteResult(''); setRewriteError(''); setRewriteJobId(''); setExtractMsg(''); setPreviewItem(item); }}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 overflow-hidden rounded bg-muted" style={{ width: '60px', aspectRatio: '3/4' }}>
            {coverOf(item) ? <CoverImg src={coverOf(item)} mediaType={item.media_type} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground/50">{item.media_type === 'video' ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}</div>}
          </div>
          <div className="min-w-0">
            <ClampedTitle text={item.title || item.description || '无标题'} lines={1} />
            <div className="text-xs text-muted-foreground">{item.account_name || item.account_uid || ''}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">{platformBadge(item.platform)}{item.deleted ? <span className="rounded bg-gray-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">用户已删除</span> : null}</div>
      </td>
      {isAdmin && <td className="px-3 py-2.5 text-xs text-muted-foreground">{item.user_name || item.user_username || '—'}</td>}
      <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtTime(item.create_time)}</td>
      <td className="px-3 py-2.5 text-xs text-right">{fmt(item.digg_count)}</td>
      <td className="px-3 py-2.5 text-xs text-right">{fmt(secondStat(item).value)}</td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {item.tags?.length > 0 ? item.tags.slice(0, 2).map(t => (
            <span key={t.id} className="inline-flex items-center rounded-sm px-1 py-0.5 text-[10px] font-medium" style={{ backgroundColor: t.color + '20', color: t.color }}>{t.name}</span>
          )) : <span className="text-xs text-muted-foreground">—</span>}
          {item.tags?.length > 2 && <span className="text-[10px] text-muted-foreground">+{item.tags.length - 2}</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openTagEdit(item); }}><Tag className="h-3 w-3 mr-1" />标签</Button>
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">内容库</h1>
          <p className="mt-1 text-sm text-muted-foreground">已提取、下载和转录的内容管理</p>
        </div>
        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <div className="flex rounded-md border bg-muted/30 p-0.5">
            <button onClick={() => setViewMode('card')} className={`rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${viewMode === 'card' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}><LayoutGrid className="h-3.5 w-3.5" /></button>
            <button onClick={() => setViewMode('list')} className={`rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}><List className="h-3.5 w-3.5" /></button>
          </div>
          <Button variant="outline" size="icon" onClick={load} title="刷新"><RefreshCw className="h-4 w-4" /></Button>
          <Button className="bg-red-500 text-white shadow-sm hover:bg-red-600" onClick={() => setViralOpen(true)}><Zap className="h-4 w-4" />智能抓取</Button>
          <Button onClick={openAddContent}><Plus className="h-4 w-4" />手动抓取</Button>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {[{ key: '', label: '全部' }, { key: 'douyin', label: '抖音' }, { key: 'xiaohongshu', label: '小红书' }, { key: 'wechat', label: '视频号' }].map((t) => {
            const info = PLATFORM_INFO[t.key]; const active = platformFilter === t.key;
            return <Button key={t.key} variant={active ? 'default' : 'outline'} size="sm" onClick={() => { setPlatformFilter(t.key); setPage(1); }} className="text-xs">{info && <span className={`mr-1 h-2 w-2 rounded-full ${info.color}`} />}{t.label}</Button>;
          })}
        </div>

        {/* 标签筛选 */}
        <select className="h-8 rounded-md border bg-background px-2 text-xs" value={filterTag} onChange={(e) => { setFilterTag(e.target.value); setPage(1); }}>
          <option value="">全部标签</option>
          {allTags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>

        {/* 用户筛选（管理员） */}
        {isAdmin && (
          <select className="h-8 rounded-md border bg-background px-2 text-xs" value={filterUserId} onChange={(e) => { setFilterUserId(Number(e.target.value)); setPage(1); }}>
            <option value={0}>全部用户</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-muted-foreground">排序</label>
          <select className="h-8 rounded-md border bg-background px-2 text-xs" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
            <option value="added_at">添加时间</option><option value="create_time">发布时间</option>
            <option value="digg_count">点赞数</option><option value="play_count">播放量</option>
          </select>
        </div>
      </div>

      {/* 内容 */}
      {loading ? (
        viewMode === 'card' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        ) : <Skeleton className="h-48 rounded-lg" />
      ) : items.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground"><FileText className="mb-3 h-8 w-8" /><p className="text-sm">内容库为空</p></CardContent></Card>
      ) : viewMode === 'card' ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">{items.map(renderCard)}</div>
        </>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/50 text-left">
              <th className="px-3 py-2.5 font-medium">内容</th>
              <th className="px-3 py-2.5 font-medium">平台</th>
              {isAdmin && <th className="px-3 py-2.5 font-medium">用户</th>}
              <th className="px-3 py-2.5 font-medium">时间</th>
              <th className="px-3 py-2.5 font-medium text-right">点赞</th>
              <th className="px-3 py-2.5 font-medium text-right">播放/收藏/转发</th>
              <th className="px-3 py-2.5 font-medium">标签</th>
              <th className="px-3 py-2.5"></th>
            </tr></thead>
            <tbody>{items.map(renderRow)}</tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" />上一页</Button>
          <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>下一页<ChevronRight className="h-4 w-4" /></Button>
          <div className="ml-3 flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">跳至</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doJumpPage(); }}
              className="h-8 w-16 rounded-md border border-input bg-background px-2 text-center text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              aria-label="跳转页码"
            />
            <Button variant="outline" size="sm" className="h-8" onClick={doJumpPage}>跳转</Button>
          </div>
        </div>
      )}

      {/* 预览弹窗 */}
      {previewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6" onClick={() => setPreviewItem(null)}>
          <div className="flex max-h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border bg-background shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold">内容详情</h3>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreviewItem(null)}><X className="h-4 w-4" /></Button>
            </div>
            {/* 主体：lg+ 左右分栏（左媒体 / 右信息），<lg 上下堆叠 */}
            <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
              {/* 左：媒体栏（lg+ 宽度按媒体比例随高度自适应，竖版视频撑满无黑边） */}
              <div ref={mediaColRef} className="flex w-full flex-shrink-0 flex-col gap-2 bg-black p-3 lg:w-[340px] lg:border-r lg:p-4">
                <div ref={mediaBoxRef} className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-black">
                  {previewItem.media_type === 'video' ? (
                    <video
                      key={previewItem.video_id}
                      src={`/api/media/video/${previewItem.video_id}?token=${getToken() || ''}`}
                      poster={coverOf(previewItem) || undefined}
                      controls
                      preload="metadata"
                      className="max-h-[42vh] w-full object-contain lg:max-h-full"
                    />
                  ) : previewItem.image_list && previewItem.image_list.length > 0 ? (
                    <ImageCarousel images={previewItem.image_list} poster={previewItem.cover_url} />
                  ) : previewItem.cover_url ? (
                    <div className="h-full max-h-[42vh] w-full overflow-hidden rounded-lg lg:max-h-full">
                      <CoverImg src={previewItem.cover_url} mediaType={previewItem.media_type} className="h-full w-full object-cover" />
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    {previewItem.media_type === 'video' ? (
                      <>
                        <DownloadRingMini status={previewItem.download_status || 'pending'} />
                        <span className={statusText(previewItem).cls}>{statusText(previewItem).text}</span>
                      </>
                    ) : '图文内容'}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {previewItem.media_type === 'image' && (() => {
                      const localSrc = previewItem.download_status === 'downloaded' && previewItem.video_id
                        ? `/api/media/video/${previewItem.video_id}?token=${getToken() || ''}` : '';
                      const remoteSrc = (previewItem.video_url || '').split('||')[0] || '';
                      const musicSrc = localSrc || remoteSrc;
                      return musicSrc ? <MusicToggle src={musicSrc} /> : null;
                    })()}
                    <span>{PLATFORM_INFO[previewItem.platform]?.label || previewItem.platform}{previewItem.duration > 0 ? ` · ${Math.floor(previewItem.duration / 60)}:${String(previewItem.duration % 60).padStart(2, '0')}` : ''}</span>
                  </span>
                </div>
              </div>
              {/* 右：顶栏（标题/数据/标签常驻）+ 滚动区（含互动趋势全宽） */}
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex-shrink-0 border-b bg-background px-5 py-3">
                  <h4 className="font-medium leading-snug">{previewItem.title || previewItem.description || '无标题'}</h4>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    {platformBadge(previewItem.platform)}
                    {previewItem.deleted ? <span className="inline-flex items-center rounded bg-gray-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">用户已删除</span> : null}
                    {isAdmin && previewItem.user_name && <Badge variant="outline">{previewItem.user_name}</Badge>}
                    {platformStats(previewItem).map((s, i) => {
                      const k = TREND_KEY[s.label];
                      const pts = trend.points;
                      const d = pts.length >= 2 && k ? ((pts[pts.length - 1][k] as number) || 0) - ((pts[0][k] as number) || 0) : 0;
                      return (
                        <span key={i} className="inline-flex items-center gap-1" title={s.label}>
                          <s.icon className="h-3.5 w-3.5" />{fmt(s.value)}
                          {pts.length >= 2 && d > 0 && <span className="text-[10px] font-medium text-emerald-500" title="较首次采样">+{d}</span>}
                          {pts.length >= 2 && d < 0 && <span className="text-[10px] font-medium text-red-500" title="较首次采样">{d}</span>}
                        </span>
                      );
                    })}
                  </div>
                  {previewItem.tags?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      <span className="mr-1 text-xs text-muted-foreground">标签：</span>
                      {previewItem.tags.map(t => <span key={t.id} className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: t.color + '20', color: t.color }}>{t.name}</span>)}
                    </div>
                  )}
                </div>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
                  {/* 互动趋势（标题下方全宽，上下排列；h-36 容纳 y 刻度 + 折线 + x 日期行 + 图例） */}
                  <div className="h-36">
                    <TrendChartMini points={trend.points} loading={trend.loading} error={trend.error} />
                  </div>
                  {/* 文稿区 + 改写对比区：图文=正文 / 视频=逐字稿；改写完成后 grid 列 1fr+0fr → 1fr+1fr 平滑过渡（逐字稿收缩 / 改写稿展开动画） */}
                  {previewItem.media_type === 'video' ? (
                    previewItem.transcript ? (
                      <div>
                        <div
                          className="grid overflow-hidden rounded-lg border bg-muted/30 transition-[grid-template-columns] duration-500 ease-in-out"
                          style={{ gridTemplateColumns: rewriteResult ? '1fr 1fr' : '1fr 0fr' }}
                        >
                          {/* 左：逐字稿（改前全宽 → 改后收缩到一半） */}
                          <div className="min-w-0 overflow-hidden">
                            <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
                              <span className="flex items-center gap-2 text-xs font-medium">逐字稿<Badge variant="success" className="text-[10px]">已转录</Badge></span>
                              {rewriteResult && <Badge variant="success" className="text-[10px]">原文</Badge>}
                            </div>
                            <p className="max-h-[38vh] overflow-y-auto whitespace-pre-wrap p-3 text-sm text-muted-foreground">{previewItem.transcript}</p>
                          </div>
                          {/* 右：改写稿（改前 0 宽隐藏 → 改后展开） */}
                          <div className="min-w-0 overflow-hidden">
                            {rewriteResult && (
                              <div className="h-full">
                                <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
                                  <span className="text-xs font-medium">改写稿</span>
                                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => handleDownloadRewrite(rewriteResult)}>下载文案</Button>
                                </div>
                                <p className="max-h-[38vh] overflow-y-auto whitespace-pre-wrap border-t border-emerald-500/20 bg-emerald-500/[0.03] p-3 text-sm text-muted-foreground">{rewriteResult}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {rewriteMsg && <p className="mt-2 text-xs text-muted-foreground">{rewriteMsg}</p>}
                        {rewriteError && <p className="mt-2 text-xs text-destructive">{rewriteError}</p>}
                      </div>
                    ) : (
                      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">该视频暂无逐字稿（下载完成后系统自动转录）</div>
                    )
                  ) : previewItem.description ? (
                    <div>
                      <h5 className="mb-1.5 text-sm font-medium">正文</h5>
                      <p className="max-h-[38vh] overflow-y-auto whitespace-pre-wrap rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">{previewItem.description}</p>
                    </div>
                  ) : null}
                  {/* 操作按钮（AI 改写按钮与下载/标签同一排、靠右） */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {previewItem.media_type !== 'video' && previewItem.platform !== 'xiaohongshu' && (
                        <Button variant="outline" size="sm" onClick={() => handleExtractText(previewItem)} disabled={extractingText} title="调详情接口拉取完整正文（扣提取积分）">
                          {extractingText ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} 提取文案
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleDownload(previewItem)} disabled={downloading}>{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} 下载到本地</Button>
                      <Button variant="outline" size="sm" onClick={() => openTagEdit(previewItem)}><Tag className="h-4 w-4" />管理标签</Button>
                      {extractMsg && previewItem.media_type !== 'video' && previewItem.platform !== 'xiaohongshu' && <span className="text-xs text-muted-foreground">{extractMsg}</span>}
                      {downloadMsg && <span className="text-xs text-destructive">{downloadMsg}</span>}
                    </div>
                    {previewItem.media_type === 'video' && !rewriteResult && (
                      <Button size="sm" variant={rewriting ? 'outline' : 'default'} onClick={() => handleRewrite(previewItem)} disabled={rewriting || !previewItem.transcript} title={!previewItem.transcript ? '需先转录生成逐字稿' : ''}>
                        {rewriting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {rewriting ? '改写中…' : 'AI 改写'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 标签编辑弹窗 */}
      {tagEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setTagEditOpen(false)}>
          <div className="w-full max-w-md rounded-lg border bg-background shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="font-medium">管理标签</div>
              <Button variant="ghost" size="sm" onClick={() => setTagEditOpen(false)}>关闭</Button>
            </div>
            <div className="p-4 space-y-3">
              {/* 当前选中标签 */}
              <div className="flex flex-wrap gap-1.5">
                {allTags.map(t => {
                  const selected = editItemTags.some(et => et.id === t.id);
                  return (
                    <button key={t.id} onClick={() => setEditItemTags(prev => selected ? prev.filter(et => et.id !== t.id) : [...prev, t])}
                      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${selected ? 'text-white' : 'border border-border text-muted-foreground hover:border-foreground'}`}
                      style={selected ? { backgroundColor: t.color, borderColor: t.color } : {}}>
                      {t.name}{selected && <X className="h-3 w-3" />}
                    </button>
                  );
                })}
              </div>
              {/* 新建标签 */}
              <div className="flex gap-2">
                <Input placeholder="新标签名" value={newTagName} onChange={e => setNewTagName(e.target.value)} className="h-8 text-xs" onKeyDown={e => e.key === 'Enter' && createNewTag()} />
                <Button variant="outline" size="sm" onClick={createNewTag}><Plus className="h-3 w-3" />新建</Button>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" size="sm" onClick={() => setTagEditOpen(false)}>取消</Button>
                <Button size="sm" onClick={saveTags}>保存标签</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 爆款内容爬取弹窗 */}
      <ViralFetchModal
        open={viralOpen}
        onClose={() => setViralOpen(false)}
        onImported={() => {
          // 导入成功后刷新内容库并回到第一页
          setPage(1);
          load();
        }}
      />

      {/* 添加单条内容弹窗 */}
      {addContentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAddContentOpen(false)}>
          <div className="w-full max-w-xl rounded-lg border bg-background shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2 font-medium">
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                手动抓取
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAddContentOpen(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">平台</label>
                <select className="h-8 w-full rounded-md border bg-background px-2 text-sm" value={addPlatform} onChange={(e) => setAddPlatform(e.target.value)}>
                  <option value="douyin">抖音</option>
                  <option value="xiaohongshu">小红书</option>
                  <option value="wechat">视频号</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">分享链接</label>
                <Input
                  placeholder="粘贴分享链接…"
                  value={addUrl}
                  onChange={(e) => setAddUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddContent(); }}
                  autoFocus
                />
              </div>
              {addMessage && (
                <div className={`text-sm ${addMessage.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : addMessage.type === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {addMessage.text}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1 border-t">
                <Button variant="ghost" size="sm" onClick={() => setAddContentOpen(false)}>取消</Button>
                <Button size="sm" onClick={handleAddContent} disabled={adding}>
                  {adding ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />提取中…</> : <><Download className="h-3.5 w-3.5 mr-1" />提取</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}